/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * One user's record (v2, /settings/users/:userId): profile, access (base and
 * add-on roles, teams, seats) and activity. Profile and access edit one draft
 * that saves in a single PUT /users/{id}/access, which applies everything or
 * nothing and says which part failed.
 *
 * What a person may change follows their capabilities, as the API checks:
 * MANAGE_USERS for profile, seats and status; MANAGE_ROLES for roles;
 * MANAGE_TEAMS for teams.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ArrowLeft, Briefcase, Clock, Download, KeyRound, Mail, Power, Trash2, UserRound } from "lucide-react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import { useCapability } from "auth/usePermission";
import RecordHero from "components/v2/record/RecordHero";
import { SettingsSection, ToneChip } from "components/v2/settings/SettingsBlocks";
import { v2 as t } from "styles/tokens/v2";
import { relativeTime } from "layouts/crm/account360/format";
import { STATUS, accessChanges, apiErrorText, fullName, seatName } from "./usersModel";

const PROFILE_FIELDS = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name", required: true },
  { key: "email", label: "Email", required: true, type: "email" },
  { key: "designation", label: "Title" },
  { key: "department", label: "Department" },
];

const PART_TAB = { profile: "profile", status: "profile", roles: "access", teams: "access", seats: "access" };

function UserRecord() {
  const { userId } = useParams();
  const id = Number(userId);
  const navigate = useNavigate();
  const { axiosInstance, user: me } = useAuth();
  const canManage = useCapability("MANAGE_USERS");
  const canRoles = useCapability("MANAGE_ROLES");
  const canTeams = useCapability("MANAGE_TEAMS");

  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [seatModules, setSeatModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  const [tab, setTab] = useState("access");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [toast, setToast] = useState(null);
  const [logRange, setLogRange] = useState({ from: moment().subtract(7, "days").format("YYYY-MM-DD"), to: moment().format("YYYY-MM-DD") });
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/users/${id}/access`);
      setOriginal(data);
      setDraft(data);
      setLoadError("");
    } catch (e) {
      setLoadError(apiErrorText(e, "Could not load this user."));
    }
  }, [axiosInstance, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axiosInstance.get("/license/module-seats").then(({ data }) => setSeatModules(data || [])).catch(() => setSeatModules([]));
    axiosInstance.get("/users/assignable-roles").then(({ data }) => setRoles(data || [])).catch(() => setRoles([]));
    axiosInstance.get("/teams").then(({ data }) => setTeams(data || [])).catch(() => setTeams([]));
    axiosInstance.get("/users/directory").then(({ data }) => setPeople(data?.users || [])).catch(() => setPeople([]));
  }, [axiosInstance]);

  const changes = useMemo(() => (original && draft ? accessChanges(original, draft) : {}), [original, draft]);
  const dirty = Object.keys(changes).length > 0;
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async (extra = {}) => {
    const body = { ...changes, ...extra };
    if (!Object.keys(body).length) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await axiosInstance.put(`/users/${id}/access`, body);
      setOriginal(data);
      setDraft(data);
      setToast({ severity: "success", text: "Saved." });
    } catch (e) {
      const part = e?.response?.data?.part;
      setSaveError({ text: apiErrorText(e, "Could not save."), part });
      if (part && PART_TAB[part]) setTab(PART_TAB[part]);
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    try {
      await axiosInstance.post("/forgot-password/", { username: original.username });
      setToast({ severity: "success", text: `A password reset link was sent to ${original.email}.` });
    } catch (e) {
      setToast({ severity: "error", text: apiErrorText(e, "Could not send the reset link.") });
    }
  };

  const downloadLogs = async () => {
    setDownloading(true);
    try {
      const response = await axiosInstance.get("/log/download", {
        params: { start_date: logRange.from, end_date: logRange.to, username: original.username },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `logs_${original.username}_${logRange.from}_to_${logRange.to}.zip`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setToast({ severity: "error", text: apiErrorText(e, "No logs for those dates, or the download failed.") });
    } finally {
      setDownloading(false);
    }
  };

  if (loadError) {
    return (
      <DashboardLayout>
        <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate("/settings?tab=users")} sx={{ mb: 2 }}>Users and seats</Button>
        <Alert severity="error">{loadError}</Alert>
      </DashboardLayout>
    );
  }
  if (!draft) {
    return (
      <DashboardLayout>
        <Box sx={{ py: 10, display: "grid", placeItems: "center" }}>
          <CircularProgress size={28} />
        </Box>
      </DashboardLayout>
    );
  }

  const status = STATUS[original.status] || STATUS.active;
  const isSelf = me?.id === original.id;
  const baseRoles = roles.filter((r) => r.role_kind === "base");
  const addonRoles = roles.filter((r) => r.role_kind === "addon");
  const managerOptions = people.filter((p) => p.id !== original.id && p.is_active);
  const draftTeamIds = (draft.teams || []).map((tm) => tm.id);

  const setTeam = (teamId, patch) =>
    set({
      teams: (draft.teams || []).map((tm) => {
        if (tm.id === teamId) return { ...tm, ...patch };
        return patch.is_primary ? { ...tm, is_primary: false } : tm;
      }),
    });

  const field = { size: "small", fullWidth: true };

  return (
    <DashboardLayout>
      <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate("/settings?tab=users")} sx={{ mb: 1.5 }}>
        Users and seats
      </Button>

      <RecordHero
        icon={UserRound}
        eyebrow="User"
        title={fullName(original)}
        label="User summary"
        badges={
          <>
            <ToneChip tone={status.tone}>{status.label}</ToneChip>
            {original.base_role && <ToneChip tone="info">{original.base_role.name}</ToneChip>}
            {(original.addon_roles || []).map((r) => (
              <ToneChip key={r.id} tone="purple">{r.name}</ToneChip>
            ))}
          </>
        }
        meta={[
          { key: "email", icon: Mail, content: original.email },
          { key: "title", icon: Briefcase, content: [original.designation, original.department].filter(Boolean).join(" · ") || null },
          { key: "login", icon: Clock, content: original.last_login ? `Last signed in ${relativeTime(original.last_login)}` : "Never signed in" },
        ]}
        actions={
          canManage ? (
            <>
              <Button variant="outlined" startIcon={<KeyRound size={16} />} onClick={resetPassword}>Send password reset</Button>
              {!isSelf && (
                <Button
                  variant="outlined"
                  color={original.is_active ? "error" : "primary"}
                  startIcon={<Power size={16} />}
                  disabled={saving || dirty}
                  title={dirty ? "Save or discard your changes first" : undefined}
                  onClick={() => save({ is_active: !original.is_active })}
                >
                  {original.is_active ? "Deactivate" : "Activate"}
                </Button>
              )}
            </>
          ) : null
        }
      />

      <Box sx={{ borderBottom: `1px solid ${t.border}`, mb: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="User sections">
          <Tab value="access" label="Access" />
          <Tab value="profile" label="Profile" />
          <Tab value="activity" label="Activity" />
        </Tabs>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          Nothing was saved. {saveError.text}
        </Alert>
      )}

      {tab === "profile" && (
        <SettingsSection title="Profile" summary={canManage ? "Name, contact and reporting line." : "Only people who manage users can change this."}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, py: 1 }}>
            {PROFILE_FIELDS.map((f) => (
              <TextField
                key={f.key}
                {...field}
                label={f.label}
                type={f.type || "text"}
                required={f.required}
                value={draft[f.key] || ""}
                disabled={!canManage}
                onChange={(e) => set({ [f.key]: e.target.value })}
              />
            ))}
            <Autocomplete
              options={managerOptions}
              getOptionLabel={(p) => fullName(p)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={managerOptions.find((p) => p.id === draft.manager_id) || (draft.manager_id ? { id: draft.manager_id, full_name: draft.manager_name } : null)}
              onChange={(_, v) => set({ manager_id: v ? v.id : null, manager_name: v ? fullName(v) : null })}
              disabled={!canManage}
              renderInput={(params) => <TextField {...params} size="small" label="Manager" />}
            />
          </Box>
        </SettingsSection>
      )}

      {tab === "access" && (
        <>
          <SettingsSection
            title="Roles"
            summary={canRoles ? "The base role sets what they can do; add-on roles add to it." : "Only people who manage roles can change these."}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, py: 1 }}>
              <TextField
                select
                {...field}
                label="Base role"
                required
                value={draft.base_role?.id ?? ""}
                disabled={!canRoles || !baseRoles.length}
                onChange={(e) => {
                  const r = baseRoles.find((x) => x.id === e.target.value);
                  set({ base_role: r ? { id: r.id, name: r.name, is_super: r.is_super } : null });
                }}
              >
                {baseRoles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </TextField>
              <Autocomplete
                multiple
                options={addonRoles}
                getOptionLabel={(r) => r.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={(draft.addon_roles || []).map((r) => addonRoles.find((x) => x.id === r.id) || r)}
                onChange={(_, v) => set({ addon_roles: v.map((r) => ({ id: r.id, name: r.name, is_super: r.is_super })) })}
                disabled={!canRoles}
                renderInput={(params) => <TextField {...params} size="small" label="Add-on roles" />}
              />
            </Box>
          </SettingsSection>

          <SettingsSection
            title="Teams"
            summary={canTeams ? "Leads see their team's records. One team can be the primary one." : "Only people who manage teams can change these."}
          >
            <Box sx={{ py: 1 }}>
              {(draft.teams || []).length === 0 && <Typography sx={{ fontSize: 14, color: t.text2, mb: 1.5 }}>Not in any team.</Typography>}
              {(draft.teams || []).map((tm) => (
                <Box key={tm.id} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, py: 1, borderBottom: `1px solid ${t.border}` }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: t.text, minWidth: 180, flex: 1 }}>{tm.name}</Typography>
                  <TextField
                    select
                    size="small"
                    label="Membership"
                    value={tm.membership || "member"}
                    disabled={!canTeams}
                    onChange={(e) => setTeam(tm.id, { membership: e.target.value })}
                    sx={{ width: 150 }}
                  >
                    <MenuItem value="member">Member</MenuItem>
                    <MenuItem value="lead">Lead</MenuItem>
                  </TextField>
                  <FormControlLabel
                    control={<Radio size="small" checked={Boolean(tm.is_primary)} disabled={!canTeams} onChange={() => setTeam(tm.id, { is_primary: true })} />}
                    label="Primary"
                  />
                  {canTeams && (
                    <IconButton aria-label={`Remove from ${tm.name}`} onClick={() => set({ teams: draft.teams.filter((x) => x.id !== tm.id) })}>
                      <Trash2 size={16} />
                    </IconButton>
                  )}
                </Box>
              ))}
              {canTeams && (
                <TextField
                  select
                  size="small"
                  label="Add to a team"
                  value=""
                  onChange={(e) => {
                    const team = teams.find((x) => x.id === e.target.value);
                    if (team) set({ teams: [...(draft.teams || []), { id: team.id, name: team.name, membership: "member", is_primary: !(draft.teams || []).length }] });
                  }}
                  sx={{ width: 260, mt: 1.5 }}
                  disabled={!teams.some((x) => !draftTeamIds.includes(x.id))}
                >
                  {teams.filter((x) => !draftTeamIds.includes(x.id)).map((x) => (
                    <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          </SettingsSection>

          {seatModules.length > 0 && (
            <SettingsSection title="Add-on seats" summary="Seats for the add-ons your plan includes.">
              <Box sx={{ py: 0.5 }}>
                {seatModules.map((m, i) => {
                  const had = Boolean(original.seats?.[m.module_code]);
                  const on = Boolean(draft.seats?.[m.module_code]);
                  const left = m.seats_remaining + (had ? 1 : 0) - (on ? 1 : 0);
                  const blocked = !on && (left <= 0 || !original.is_active || original.is_support_user);
                  return (
                    <Box key={m.module_code} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25, borderTop: i ? `1px solid ${t.border}` : "none" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: t.text }}>{seatName(m)}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.text2 }}>
                          {!original.is_active && !on ? "Activate this user to give them a seat." : `${Math.max(0, left)} of ${m.total_seats} seats left`}
                        </Typography>
                      </Box>
                      <Switch
                        checked={on}
                        disabled={!canManage || blocked}
                        onChange={(e) => set({ seats: { ...(draft.seats || {}), [m.module_code]: e.target.checked } })}
                        inputProps={{ "aria-label": `${seatName(m)} seat` }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </SettingsSection>
          )}
        </>
      )}

      {tab === "activity" && (
        <SettingsSection title="Activity log" summary="Download what this user did in a date range.">
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, py: 1 }}>
            <TextField size="small" type="date" label="From" value={logRange.from} InputLabelProps={{ shrink: true }} onChange={(e) => setLogRange((r) => ({ ...r, from: e.target.value }))} />
            <TextField size="small" type="date" label="To" value={logRange.to} InputLabelProps={{ shrink: true }} onChange={(e) => setLogRange((r) => ({ ...r, to: e.target.value }))} />
            <MDButton variant="gradient" color="info" startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <Download size={16} />} disabled={downloading || !logRange.from || !logRange.to} onClick={downloadLogs}>
              Download logs
            </MDButton>
          </Box>
        </SettingsSection>
      )}

      {dirty && tab !== "activity" && (
        <Box
          role="region"
          aria-label="Unsaved changes"
          sx={{
            position: "sticky",
            bottom: 16,
            zIndex: 5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: "12px",
            bgcolor: t.header,
            color: "#FFFFFF",
            boxShadow: "0 8px 24px rgba(14, 35, 72, 0.24)",
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#FFFFFF", flex: 1 }}>
            Unsaved changes to {fullName(original)}. They save together, or not at all.
          </Typography>
          <Button onClick={() => { setDraft(original); setSaveError(null); }} disabled={saving} sx={{ color: "#FFFFFF" }}>
            Discard
          </Button>
          <Button variant="contained" color="warning" onClick={() => save()} disabled={saving} sx={{ bgcolor: t.gold, color: t.header, "&:hover": { bgcolor: "#E6A200" } }}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </DashboardLayout>
  );
}

// The page exists in the new look only; classic keeps the users table.
export default function UserRecordV2() {
  const isV2 = useTheme()?.pearlUi === "v2";
  return isV2 ? <UserRecord /> : <Navigate to="/settings?tab=users" replace />;
}
